import { useState, useEffect, useCallback } from "react";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Edit, Upload, Download, FileText, Building2, Receipt, Loader2, Eye, BanknoteIcon } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";
import { todayCR } from "../utils/dateHelpers";

const fmtCRC = (n) => `₡${(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SuppliersInvoices({ user }) {
  const { selectedBox, boxes } = useBox();
  const [tab, setTab] = useState("invoices");

  // Suppliers
  const [suppliers, setSuppliers] = useState([]);
  const [supplierModal, setSupplierModal] = useState(false);
  const [supplierForm, setSupplierForm] = useState({ id: null, name: "", cedula_juridica: "", phone: "", email: "", bank_iban: "", notes: "" });

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    supplier_id: "",
    invoice_number: "",
    invoice_date: todayCR(),
    iva_mode: "global",
    global_iva_percent: 13,
    notes: "",
    items: [],
  });
  const [importing, setImporting] = useState(false);

  // Inventory products for linking
  const [products, setProducts] = useState([]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/suppliers");
      setSuppliers(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error cargando proveedores"));
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const url = selectedBox?.id ? `/purchase-invoices?box_id=${selectedBox.id}` : "/purchase-invoices";
      const res = await axiosInstance.get(url);
      setInvoices(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error cargando facturas"));
    }
  }, [selectedBox?.id]);

  const fetchProducts = useCallback(async () => {
    if (!selectedBox?.id) return;
    try {
      const res = await axiosInstance.get(`/inventory/products?box_id=${selectedBox.id}`);
      setProducts(res.data?.products || res.data || []);
    } catch (e) {
      // silent
    }
  }, [selectedBox?.id]);

  useEffect(() => {
    fetchSuppliers();
    fetchInvoices();
    fetchProducts();
  }, [fetchSuppliers, fetchInvoices, fetchProducts]);

  // ------------ SUPPLIERS ------------

  const openSupplier = (s = null) => {
    setSupplierForm(s || { id: null, name: "", cedula_juridica: "", phone: "", email: "", bank_iban: "", notes: "" });
    setSupplierModal(true);
  };

  const saveSupplier = async () => {
    if (!supplierForm.name.trim()) {
      toast.error("Nombre requerido");
      return;
    }
    try {
      if (supplierForm.id) {
        await axiosInstance.patch(`/suppliers/${supplierForm.id}`, supplierForm);
        toast.success("Proveedor actualizado");
      } else {
        await axiosInstance.post("/suppliers", supplierForm);
        toast.success("Proveedor creado");
      }
      setSupplierModal(false);
      fetchSuppliers();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al guardar"));
    }
  };

  const deleteSupplier = async (s) => {
    if (!window.confirm(`¿Desactivar proveedor "${s.name}"?`)) return;
    try {
      await axiosInstance.delete(`/suppliers/${s.id}`);
      toast.success("Proveedor desactivado");
      fetchSuppliers();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al eliminar"));
    }
  };

  // ------------ INVOICES ------------

  const openInvoice = () => {
    setInvoiceForm({
      supplier_id: "",
      invoice_number: "",
      invoice_date: todayCR(),
      iva_mode: "global",
      global_iva_percent: 13,
      notes: "",
      items: [{ inventory_item_id: null, code: "", description: "", quantity: 1, unit_cost: 0, iva_percent: 13 }],
    });
    setInvoiceModal(true);
  };

  const updateItem = (idx, key, value) => {
    const items = [...invoiceForm.items];
    items[idx] = { ...items[idx], [key]: value };
    setInvoiceForm({ ...invoiceForm, items });
  };

  const addItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, { inventory_item_id: null, code: "", description: "", quantity: 1, unit_cost: 0, iva_percent: 13 }],
    });
  };

  const removeItem = (idx) => {
    setInvoiceForm({ ...invoiceForm, items: invoiceForm.items.filter((_, i) => i !== idx) });
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedBox?.id) {
      toast.error("Selecciona una caja primero");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("box_id", selectedBox.id);
      const res = await axiosInstance.post("/purchase-invoices/import-items", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInvoiceForm((prev) => ({ ...prev, items: [...prev.items.filter((it) => it.description), ...res.data.items] }));
      toast.success(`${res.data.imported} artículos importados`);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} errores: ver consola`);
    } catch (err) {
      toast.error(getErrorMessage(err, "Error importando Excel"));
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axiosInstance.get("/purchase-invoices/template/excel");
      const bin = atob(res.data.excel_base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error descargando plantilla"));
    }
  };

  const calcSubtotal = () => invoiceForm.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_cost) || 0), 0);
  const calcIva = () => {
    if (invoiceForm.iva_mode === "global") {
      return calcSubtotal() * (Number(invoiceForm.global_iva_percent) || 0) / 100;
    }
    return invoiceForm.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_cost) || 0) * (Number(it.iva_percent) || 0) / 100, 0);
  };

  const saveInvoice = async () => {
    if (!invoiceForm.supplier_id) { toast.error("Selecciona proveedor"); return; }
    if (!invoiceForm.invoice_number.trim()) { toast.error("Número de factura requerido"); return; }
    if (!invoiceForm.items.length || !invoiceForm.items.some((it) => it.description)) {
      toast.error("Agrega al menos un artículo"); return;
    }
    if (!selectedBox?.id) { toast.error("Selecciona una caja"); return; }

    try {
      const payload = {
        ...invoiceForm,
        box_id: selectedBox.id,
        items: invoiceForm.items.filter((it) => it.description),
      };
      await axiosInstance.post("/purchase-invoices", payload);
      toast.success("Factura registrada y stock actualizado");
      setInvoiceModal(false);
      fetchInvoices();
      fetchProducts();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al guardar factura"));
    }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Eliminar factura #${inv.invoice_number}? Se revertirá el stock del inventario.`)) return;
    try {
      await axiosInstance.delete(`/purchase-invoices/${inv.id}`);
      toast.success("Factura eliminada y stock revertido");
      fetchInvoices();
      fetchProducts();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al eliminar"));
    }
  };

  const generateExpenseFromInvoice = async (inv) => {
    if (!window.confirm(`Generar gasto pendiente desde la factura #${inv.invoice_number} por ${fmtCRC(inv.total)}?`)) return;
    try {
      const res = await axiosInstance.post(`/purchase-invoices/${inv.id}/generate-expense`);
      toast.success(`Gasto creado: ${res.data.expense_id.slice(0, 8)}...`);
      fetchInvoices();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al generar gasto"));
    }
  };

  const viewSamplePDF = async (kind) => {
    try {
      const url = kind === "fe"
        ? "/purchase-invoices-samples/fe-pdf"
        : "/purchase-invoices-samples/simplified-pdf";
      const res = await axiosInstance.get(url);
      const bin = atob(res.data.pdf_base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: "application/pdf" });
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(objUrl), 10000);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al generar ejemplo"));
    }
  };

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="suppliers-invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Receipt className="w-7 h-7 text-emerald-700" /> Proveedores & Facturas de Compra
          </h1>
          <p className="text-slate-500 text-sm">
            Caja: <b>{selectedBox?.name || "Todas"}</b> · Registra facturas de proveedores para Hacienda (TribuCR).
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => viewSamplePDF("simplified")} data-testid="sample-simplified-btn">
            <Eye className="w-3.5 h-3.5 mr-1" /> Ejemplo Factura Simplificada
          </Button>
          <Button size="sm" variant="outline" onClick={() => viewSamplePDF("fe")} data-testid="sample-fe-btn">
            <Eye className="w-3.5 h-3.5 mr-1" /> Ejemplo Factura Electrónica
          </Button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setTab("invoices")}
          data-testid="tab-invoices"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "invoices" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500"
          }`}
        >
          <FileText className="w-4 h-4 inline mr-1" /> Facturas
        </button>
        <button
          onClick={() => setTab("suppliers")}
          data-testid="tab-suppliers"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "suppliers" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500"
          }`}
        >
          <Building2 className="w-4 h-4 inline mr-1" /> Proveedores
        </button>
      </div>

      {tab === "suppliers" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Proveedores ({suppliers.length})</CardTitle>
            <Button onClick={() => openSupplier()} className="bg-emerald-700 hover:bg-emerald-800" data-testid="new-supplier-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuevo Proveedor
            </Button>
          </CardHeader>
          <CardContent>
            {suppliers.length === 0 ? (
              <p className="text-sm text-slate-500 italic text-center py-6">Sin proveedores. Crea el primero.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left">Nombre</th>
                      <th className="p-2 text-left">Cédula Jurídica</th>
                      <th className="p-2 text-left">Teléfono</th>
                      <th className="p-2 text-left">Correo</th>
                      <th className="p-2 text-left">IBAN</th>
                      <th className="p-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map((s) => (
                      <tr key={s.id} className="border-b hover:bg-slate-50" data-testid={`supplier-row-${s.id}`}>
                        <td className="p-2 font-medium">{s.name}</td>
                        <td className="p-2 text-xs font-mono">{s.cedula_juridica || "—"}</td>
                        <td className="p-2 text-xs">{s.phone || "—"}</td>
                        <td className="p-2 text-xs">{s.email || "—"}</td>
                        <td className="p-2 text-xs font-mono">{s.bank_iban || "—"}</td>
                        <td className="p-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => openSupplier(s)}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          {user?.role === "master" && (
                            <Button size="sm" variant="ghost" onClick={() => deleteSupplier(s)} className="text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "invoices" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Facturas de Compra ({invoices.length})</CardTitle>
            <Button onClick={openInvoice} className="bg-emerald-700 hover:bg-emerald-800" data-testid="new-invoice-btn">
              <Plus className="w-4 h-4 mr-1" /> Registrar Factura
            </Button>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <p className="text-sm text-slate-500 italic text-center py-6">Sin facturas registradas.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left">Fecha</th>
                      <th className="p-2 text-left"># Factura</th>
                      <th className="p-2 text-left">Proveedor</th>
                      <th className="p-2 text-left">Caja</th>
                      <th className="p-2 text-right">Subtotal</th>
                      <th className="p-2 text-right">IVA</th>
                      <th className="p-2 text-right">Total</th>
                      <th className="p-2 text-center">Items</th>
                      <th className="p-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="border-b hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                        <td className="p-2 text-xs">{inv.invoice_date}</td>
                        <td className="p-2 font-mono">{inv.invoice_number}</td>
                        <td className="p-2">{inv.supplier_name}</td>
                        <td className="p-2 text-xs text-slate-500">{inv.box_name}</td>
                        <td className="p-2 text-right font-mono">{fmtCRC(inv.subtotal)}</td>
                        <td className="p-2 text-right font-mono text-slate-500">{fmtCRC(inv.iva_total)}</td>
                        <td className="p-2 text-right font-mono font-bold">{fmtCRC(inv.total)}</td>
                        <td className="p-2 text-center">{inv.items?.length || 0}</td>
                        <td className="p-2 text-right">
                          {!inv.expense_id ? (
                            <Button size="sm" variant="ghost" onClick={() => generateExpenseFromInvoice(inv)} title="Generar Gasto pendiente desde esta factura" data-testid={`generate-expense-${inv.id}`}>
                              <BanknoteIcon className="w-3.5 h-3.5 text-emerald-700" />
                            </Button>
                          ) : (
                            <span className="text-xs text-emerald-700 mr-2" title="Gasto vinculado">✓ Gasto</span>
                          )}
                          {user?.role === "master" && (
                            <Button size="sm" variant="ghost" onClick={() => deleteInvoice(inv)} className="text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SUPPLIER MODAL */}
      <Dialog open={supplierModal} onOpenChange={setSupplierModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{supplierForm.id ? "Editar" : "Nuevo"} Proveedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre *</Label>
              <Input value={supplierForm.name} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} data-testid="supplier-name-input" />
            </div>
            <div>
              <Label>Cédula Jurídica</Label>
              <Input value={supplierForm.cedula_juridica} onChange={(e) => setSupplierForm({ ...supplierForm, cedula_juridica: e.target.value })} placeholder="3-101-XXXXXX" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Teléfono</Label>
                <Input value={supplierForm.phone} onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })} />
              </div>
              <div>
                <Label>Correo</Label>
                <Input type="email" value={supplierForm.email} onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>IBAN / Cuenta Bancaria</Label>
              <Input value={supplierForm.bank_iban} onChange={(e) => setSupplierForm({ ...supplierForm, bank_iban: e.target.value })} placeholder="CRXX..." />
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={supplierForm.notes} onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierModal(false)}>Cancelar</Button>
            <Button onClick={saveSupplier} data-testid="save-supplier-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* INVOICE MODAL */}
      <Dialog open={invoiceModal} onOpenChange={setInvoiceModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrar Factura de Compra</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Proveedor *</Label>
                <Select value={invoiceForm.supplier_id} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, supplier_id: v })}>
                  <SelectTrigger data-testid="invoice-supplier-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label># Factura *</Label>
                <Input value={invoiceForm.invoice_number} onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })} data-testid="invoice-number-input" />
              </div>
              <div>
                <Label>Fecha</Label>
                <Input type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Modo de IVA</Label>
                <Select value={invoiceForm.iva_mode} onValueChange={(v) => setInvoiceForm({ ...invoiceForm, iva_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (un IVA para toda la factura)</SelectItem>
                    <SelectItem value="per_item">Por artículo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {invoiceForm.iva_mode === "global" && (
                <div>
                  <Label>IVA Global %</Label>
                  <Input type="number" step="0.01" value={invoiceForm.global_iva_percent} onChange={(e) => setInvoiceForm({ ...invoiceForm, global_iva_percent: e.target.value })} />
                </div>
              )}
            </div>

            <div className="border rounded p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-bold">Artículos ({invoiceForm.items.length})</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> Plantilla
                  </Button>
                  <label className="inline-flex">
                    <input type="file" accept=".xlsx" onChange={handleExcelImport} className="hidden" disabled={importing} />
                    <Button size="sm" variant="outline" disabled={importing} asChild>
                      <span className="cursor-pointer">
                        {importing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                        Importar Excel
                      </span>
                    </Button>
                  </label>
                  <Button size="sm" onClick={addItem}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Línea
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-1 text-left">Código</th>
                      <th className="p-1 text-left min-w-[150px]">Descripción</th>
                      <th className="p-1 text-left">Vincular Inv.</th>
                      <th className="p-1 text-right">Cant.</th>
                      <th className="p-1 text-right">Costo Unit.</th>
                      {invoiceForm.iva_mode === "per_item" && <th className="p-1 text-right">IVA %</th>}
                      <th className="p-1 text-right">Total</th>
                      <th className="p-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceForm.items.map((it, idx) => (
                      <tr key={idx} className="border-b">
                        <td><Input value={it.code} onChange={(e) => updateItem(idx, "code", e.target.value)} className="h-7 text-xs" /></td>
                        <td><Input value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="h-7 text-xs" /></td>
                        <td>
                          <Select value={it.inventory_item_id || "none"} onValueChange={(v) => updateItem(idx, "inventory_item_id", v === "none" ? null : v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">(sin vincular)</SelectItem>
                              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td><Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="h-7 text-xs text-right" /></td>
                        <td><Input type="number" step="0.01" value={it.unit_cost} onChange={(e) => updateItem(idx, "unit_cost", e.target.value)} className="h-7 text-xs text-right" /></td>
                        {invoiceForm.iva_mode === "per_item" && (
                          <td><Input type="number" step="0.01" value={it.iva_percent} onChange={(e) => updateItem(idx, "iva_percent", e.target.value)} className="h-7 text-xs text-right" /></td>
                        )}
                        <td className="p-1 text-right font-mono">{fmtCRC((Number(it.quantity) || 0) * (Number(it.unit_cost) || 0))}</td>
                        <td className="p-1 text-right">
                          <Button size="sm" variant="ghost" onClick={() => removeItem(idx)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 flex justify-end gap-6 text-sm">
                <div>Subtotal: <span className="font-mono font-bold">{fmtCRC(calcSubtotal())}</span></div>
                <div>IVA: <span className="font-mono font-bold">{fmtCRC(calcIva())}</span></div>
                <div className="text-lg">Total: <span className="font-mono font-bold text-emerald-700">{fmtCRC(calcSubtotal() + calcIva())}</span></div>
              </div>
            </div>

            <div>
              <Label>Notas</Label>
              <Input value={invoiceForm.notes} onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceModal(false)}>Cancelar</Button>
            <Button onClick={saveInvoice} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-invoice-btn">
              Guardar Factura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
